import { OnboardingStatus } from '@weave/schema-gen-ts/dist/shared/call-intelligence/plg/onboarding.pb';
import { createHotspotKeys, HOTSPOT_TARGET, ONBOARDING_FLOW } from '@frontend/product-onboarding';

export const CI_BASE_ONBOARDING = {
  id: ONBOARDING_FLOW.CI_BASE.id,
  priority: ONBOARDING_FLOW.CI_BASE.priority,
} as const;

export const CI_PRO_ONBOARDING = {
  id: ONBOARDING_FLOW.CI_PRO.id,
  priority: ONBOARDING_FLOW.CI_PRO.priority,
} as const;

export const CI_BASE_FEATURE_IDS = {
  announcementBanner: 'ci-base-onboarding-announcement',
  checklistNudge: 'ci-base-checklist-nudge',
} as const;

export const CI_PRO_FEATURE_IDS = {
  announcementBanner: 'ci-pro-onboarding-announcement',
  checklistNudge: 'ci-pro-checklist-nudge',
} as const;

export const CI_BASE_CHECKLIST_POLICY = {
  /**
   * Completed checklist remains visible for 14 days after final task completion.
   */
  completedRetentionDays: 14,

  /**
   * Incomplete checklist is removed after 30 days without task completion.
   */
  incompleteExpiryDays: 30,

  /**
   * Nudge auto-opens the checklist for a limited number of times.
   */
  maxNudges: 3,

  /**
   * Latest product ask: once per day.
   * Change to 2 if product wants the uploaded spec's 2-day cooldown.
   */
  nudgeCooldownDays: 1,
} as const;

export const CI_PRO_CHECKLIST_POLICY = {
  /**
   * Completed checklist remains visible for 14 days after final task completion.
   */
  completedRetentionDays: 14,

  /**
   * Incomplete checklist is removed after 30 days without task completion.
   */
  incompleteExpiryDays: 30,

  /**
   * Nudge auto-opens the checklist for a limited number of times.
   */
  maxNudges: 3,

  /**
   * Cooldown between nudges is 2 days.
   */
  nudgeCooldownDays: 2,
} as const;

export const TERMINAL_ONBOARDING_STATUSES: OnboardingStatus[] = [
  OnboardingStatus.ONBOARDING_STATUS_REQUIREMENTS_NOT_MET,
  OnboardingStatus.ONBOARDING_STATUS_EXPIRED,
  OnboardingStatus.ONBOARDING_STATUS_DISMISSED,
];

const CI_BASE_HOTSPOT_TARGETS = {
  CALLS_NAVIGATION_MENU: HOTSPOT_TARGET.CALLS_NAVIGATION_MENU,

  CALL_HISTORY_CALL_INSIGHT_BUTTON: HOTSPOT_TARGET.CALL_HISTORY_CALL_INSIGHT_BUTTON,

  CALL_INSIGHT_PANEL_TRANSCRIPT_TAB: HOTSPOT_TARGET.CALL_INSIGHT_PANEL_TRANSCRIPT_TAB,

  PATIENT_CONTACT_AVATAR: HOTSPOT_TARGET.PATIENT_CONTACT_AVATAR,
  PATIENT_PROFILE_LAST_CALL_CARD: HOTSPOT_TARGET.PATIENT_PROFILE_LAST_CALL_CARD,
  PATIENT_PROFILE_VIEW_HISTORY_BUTTON: HOTSPOT_TARGET.PATIENT_PROFILE_VIEW_HISTORY_BUTTON,
  PATIENT_PROFILE_HISTORY_TAB_CALL: HOTSPOT_TARGET.PATIENT_PROFILE_HISTORY_TAB_CALL,

  TOP_BAR_SETTINGS_ICON: HOTSPOT_TARGET.TOP_BAR_SETTINGS_ICON,
  SETTINGS_PAGE_USER_MANAGEMENT_GROUP: HOTSPOT_TARGET.SETTINGS_PAGE_USER_MANAGEMENT_GROUP,
  ORGANIZATION_SETTINGS_USER_TABLE_ACTION_BUTTON: HOTSPOT_TARGET.ORGANIZATION_SETTINGS_USER_TABLE_ACTION_BUTTON,
} as const;

const CI_PRO_HOTSPOT_TARGETS = {
  OVERVIEW_ACTIVE_OPPORTUNITIES_INFO_ICON: HOTSPOT_TARGET.CALL_INTEL_OVERVIEW_ACTIVE_OPPORTUNITIES_INFO_ICON,
  OVERVIEW_ACTIVE_OPPORTUNITIES_INFO_ICON_TOOLTIP_POPOVER:
    HOTSPOT_TARGET.CALL_INTEL_OVERVIEW_ACTIVE_OPPORTUNITIES_INFO_ICON_TOOLTIP_POPOVER,

  OVERVIEW_SENTIMENT_UNHAPPY_CHART_SEGMENT: HOTSPOT_TARGET.CALL_INTEL_OVERVIEW_SENTIMENT_UNHAPPY_CHART_SEGMENT,
  OVERVIEW_SENTIMENT_HAPPY_CHART_SEGMENT: HOTSPOT_TARGET.CALL_INTEL_OVERVIEW_SENTIMENT_HAPPY_CHART_SEGMENT,

  CALL_INSIGHT_BUTTON: HOTSPOT_TARGET.CALL_INTEL_CALL_INSIGHT_BUTTON,
  CALL_INSIGHT_PANEL_TRANSCRIPT_TAB: HOTSPOT_TARGET.CALL_INSIGHT_PANEL_TRANSCRIPT_TAB,

  CALL_INSIGHT_PANEL_CALL_BREAKDOWN: HOTSPOT_TARGET.CALL_INSIGHT_PANEL_CALL_BREAKDOWN,
  CALL_INSIGHT_PANEL_SCHEDULING_OUTCOME_LABEL: HOTSPOT_TARGET.CALL_INSIGHT_PANEL_SCHEDULING_OUTCOME_LABEL,
  CALL_INSIGHT_PANEL_MAKE_CORRECTION_BUTTON: HOTSPOT_TARGET.CALL_INSIGHT_PANEL_MAKE_CORRECTION_BUTTON,
  CALL_INSIGHT_PANEL_EDIT_AI_VIEW_LABEL: HOTSPOT_TARGET.CALL_INSIGHT_PANEL_EDIT_AI_VIEW_LABEL,

  CALL_INSIGHT_PANEL_FEEDBACK_BUTTON: HOTSPOT_TARGET.CALL_INSIGHT_PANEL_FEEDBACK_BUTTON,
  CALL_INSIGHT_PANEL_NEGATIVE_FEEDBACK_MODAL: HOTSPOT_TARGET.CALL_INSIGHT_PANEL_NEGATIVE_FEEDBACK_MODAL,

  FOLLOW_UP_TASKS_TAB: HOTSPOT_TARGET.CALL_INTEL_FOLLOW_UP_TASKS_TAB,
  FOLLOW_UP_SCHEDULING_TASKS_LIST_BUTTON: HOTSPOT_TARGET.CALL_INTEL_FOLLOW_UP_SCHEDULING_TASKS_LIST_BUTTON,
  FOLLOW_UP_ALL_FOLLOW_UPS_LIST_BUTTON: HOTSPOT_TARGET.CALL_INTEL_FOLLOW_UP_ALL_FOLLOW_UPS_LIST_BUTTON,
  FOLLOW_UP_TASK_ROW: HOTSPOT_TARGET.CALL_INTEL_FOLLOW_UP_TASK_ROW,

  PATIENT_CONTACT_AVATAR: HOTSPOT_TARGET.PATIENT_CONTACT_AVATAR,
  PATIENT_PROFILE_LAST_CALL_CARD: HOTSPOT_TARGET.PATIENT_PROFILE_LAST_CALL_CARD,
  PATIENT_PROFILE_VIEW_HISTORY_BUTTON: HOTSPOT_TARGET.PATIENT_PROFILE_VIEW_HISTORY_BUTTON,
  PATIENT_PROFILE_HISTORY_TAB_CALL: HOTSPOT_TARGET.PATIENT_PROFILE_HISTORY_TAB_CALL,

  TOP_BAR_SETTINGS_ICON: HOTSPOT_TARGET.TOP_BAR_SETTINGS_ICON,
  SETTINGS_PAGE_USER_MANAGEMENT_GROUP: HOTSPOT_TARGET.SETTINGS_PAGE_USER_MANAGEMENT_GROUP,
  ORGANIZATION_SETTINGS_USER_TABLE_ACTION_BUTTON: HOTSPOT_TARGET.ORGANIZATION_SETTINGS_USER_TABLE_ACTION_BUTTON,
} as const;

export const CALL_INTEL_BASE_ONBOARDING_HOTSPOT_KEYS = createHotspotKeys(
  CI_BASE_ONBOARDING.id,
  CI_BASE_HOTSPOT_TARGETS
);

export const CALL_INTEL_PRO_ONBOARDING_HOTSPOT_KEYS = createHotspotKeys(CI_PRO_ONBOARDING.id, CI_PRO_HOTSPOT_TARGETS);

export const CALL_HISTORY_PATH = '/calls/call-history';
export const CALL_INTEL_PATH = '/analytics/call-intelligence/main';
